import React, { useMemo } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import Breadcrumbs from '../Breadcrumbs/Breadcrumbs';
import SeoHead from '../SeoHead/SeoHead';
import { getRouteMeta } from '../../seo/routeMeta';
import { useSeoContext } from '../../context/SeoContext';
import { metaFromLocationSearch } from '../../seo/searchMapMeta';

/** Buy/rent landing pages with WelcomeProser (or RentWelcomeProser) — no global breadcrumb bar */
const WELCOME_PATHS = new Set([
  '/',
  '/apts-home',
  '/villas-home',
  '/plots-home',
  '/rent-apartments-home',
  '/rent-villas-home',
  '/rents-home',
]);

/** Map, list search, and “near me” flows — no global breadcrumb bar */
const LOCATION_FLOW_HIDE_BREADCRUMB =
  /^\/(apts|villas|plots)-(search-screen|location-page|location-page-map2|user-location)$|^\/rents-(location-page|apts-search-screen|villas-search-screen|location-page-map1|location-page-map2)$/;

export default function Layout() {
  const { pathname, search } = useLocation();
  const normalized = pathname.endsWith('/') && pathname.length > 1 ? pathname.slice(0, -1) : pathname;
  const hideGlobalBreadcrumb =
    WELCOME_PATHS.has(normalized) || LOCATION_FLOW_HIDE_BREADCRUMB.test(normalized);

  const { override } = useSeoContext() || {};
  const routeMeta = getRouteMeta(normalized);
  const locationFlowMeta = useMemo(
    () => metaFromLocationSearch(normalized, search),
    [normalized, search],
  );

  const title = override?.title ?? locationFlowMeta?.title ?? routeMeta.title;
  const description = override?.description ?? locationFlowMeta?.description ?? routeMeta.description;
  const canonicalPath =
    override?.canonicalPath ??
    locationFlowMeta?.canonicalPath ??
    `${pathname}${search || ''}`;
  const ogImage = override?.ogImage;
  const noindex = override?.noindex;
  const jsonLdScripts = override?.jsonLdScripts ?? [];

  return (
    <>
      <SeoHead
        title={title}
        description={description}
        canonicalPath={canonicalPath}
        ogImage={ogImage}
        noindex={noindex}
        jsonLdScripts={jsonLdScripts}
      />
      {!hideGlobalBreadcrumb && <Breadcrumbs />}
      <Outlet />
    </>
  );
}
